import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { EmployeeRef } from './hr.js'

/**
 * Employee sales commission and technician incentives (ADR-049), for every company with Sales. The earners are
 * users: the salesperson of a sales document, or the technicians who led the service work an invoice bills
 * (fixed on the invoice when it is posted). Plans price what each earner sold or did; monthly runs turn it into
 * statements, approved into a commission payable that payroll or a cash or bank payment settles.
 *
 * Each run counts, per document, earner and role, what the document is worth now (posted or void, paid or
 * not, credited or not) less what approved runs counted before; a fall on a document an earlier month paid
 * is reversed at the amount it was paid at.
 */

const dateSchema = z.iso.date()

/** A percentage with up to 4 decimals, 0–100. */
const ratePercent = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Invalid percentage')
  .refine((v) => Number(v) <= 100, 'At most 100')

/** Who a plan pays: the document's salesperson, or the technicians who led the service work it bills. */
export const COMMISSION_EARNER_ROLES = ['SALES', 'TECHNICIAN'] as const
export type CommissionEarnerRole = (typeof COMMISSION_EARNER_ROLES)[number]

/** When commission is earned: when the invoice is posted, or as the customer settles it. */
export const COMMISSION_BASES = ['INVOICED', 'COLLECTED'] as const
export type CommissionBasis = (typeof COMMISSION_BASES)[number]

/** What it is a percentage of: net sales (after discount, before VAT) or gross profit (net less cost). */
export const COMMISSION_AMOUNT_BASES = ['NET_SALES', 'GROSS_PROFIT'] as const
export type CommissionAmountBase = (typeof COMMISSION_AMOUNT_BASES)[number]

/**
 * Monthly tiers: WHOLE pays the month at the rate of the highest tier reached; MARGINAL pays each slice of the
 * month at its own tier's rate.
 */
export const COMMISSION_TIER_MODES = ['WHOLE', 'MARGINAL'] as const
export type CommissionTierMode = (typeof COMMISSION_TIER_MODES)[number]

/** A catalogue line's type; free-text lines count as SERVICE with no category (ASM-264). */
export const COMMISSION_PRODUCT_TYPES = ['GOODS', 'SERVICE'] as const
export type CommissionProductType = (typeof COMMISSION_PRODUCT_TYPES)[number]

export const commissionTierSchema = z.object({
  /** The month's base from which this tier's rate applies (the first tier starts at 0). */
  fromAmount: moneySchema.refine((v) => Number(v) >= 0, 'Cannot be negative'),
  rate: ratePercent,
})
export type CommissionTier = z.output<typeof commissionTierSchema>

/** A rate for a slice of the catalogue: a product type, a category (and its subcategories), or both. */
export const commissionPlanRateSchema = z
  .object({
    productType: z
      .enum(COMMISSION_PRODUCT_TYPES)
      .nullish()
      .transform((v) => v ?? null),
    categoryId: z
      .uuid()
      .nullish()
      .transform((v) => v ?? null),
    /** 0 leaves the slice out. */
    rate: ratePercent,
  })
  .refine((v) => v.productType !== null || v.categoryId !== null, {
    path: ['categoryId'],
    message: 'Choose a product type or a category',
  })
export type CommissionPlanRateInput = z.input<typeof commissionPlanRateSchema>

const planFields = z.object({
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  earnerRole: z.enum(COMMISSION_EARNER_ROLES),
  basis: z.enum(COMMISSION_BASES),
  amountBase: z.enum(COMMISSION_AMOUNT_BASES),
  /** The plan's rate for lines no specific rate covers (replaced by the tier rate when tiers are set). */
  rate: ratePercent,
  tierMode: z.enum(COMMISSION_TIER_MODES).default('WHOLE'),
  tiers: z.array(commissionTierSchema).max(10).default([]),
  rates: z.array(commissionPlanRateSchema).max(50).default([]),
  /** Off: its members earn nothing from the next calculation (statements already approved stay). */
  active: z.boolean().default(true),
  notes: optionalText(500),
})

const planRules = (v: z.output<typeof planFields>, ctx: z.RefinementCtx) => {
  v.tiers.forEach((t, i) => {
    const prev = v.tiers[i - 1]
    if (i === 0 && Number(t.fromAmount) !== 0)
      ctx.addIssue({
        code: 'custom',
        path: ['tiers', 0, 'fromAmount'],
        message: 'The first tier starts at 0',
      })
    if (prev && Number(t.fromAmount) <= Number(prev.fromAmount))
      ctx.addIssue({
        code: 'custom',
        path: ['tiers', i, 'fromAmount'],
        message: 'Must follow the tier before',
      })
  })
  const seen = new Set<string>()
  v.rates.forEach((r, i) => {
    const key = `${r.productType ?? ''}|${r.categoryId ?? ''}`
    if (seen.has(key))
      ctx.addIssue({ code: 'custom', path: ['rates', i, 'categoryId'], message: 'Listed twice' })
    seen.add(key)
  })
}

export const commissionPlanInputSchema = planFields.superRefine(planRules)
export type CommissionPlanInput = z.input<typeof commissionPlanInputSchema>
export type CommissionPlanValue = z.output<typeof commissionPlanInputSchema>

export const commissionPlanUpdateSchema = z.object({
  plan: commissionPlanInputSchema,
  version: versionSchema,
})
export type CommissionPlanUpdate = z.input<typeof commissionPlanUpdateSchema>

/** The first day of a month ("2026-10-01"). */
export const monthStartSchema = dateSchema.refine((v) => v.endsWith('-01'), 'Choose the first day of a month')

/** The last day of a month ("2026-10-31"). */
export const monthEndSchema = dateSchema.refine((v) => {
  const [y, m, d] = v.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m, 0)).getUTCDate() === d
}, 'Choose the last day of a month')

/**
 * A user on a plan for whole months: one plan per user and role in any month (409 COMMISSION_MEMBER_OVERLAP).
 * A document counts for the member only when it is dated inside the membership.
 */
export const commissionMemberInputSchema = z
  .object({
    userId: z.uuid(),
    effectiveFrom: monthStartSchema,
    effectiveTo: monthEndSchema.nullish().transform((v) => v ?? null),
    /** The month's sales (or work) the earner is expected to reach: shown as achievement, never a rate. */
    monthlyTarget: moneySchema
      .refine((v) => Number(v) > 0, 'Must be more than zero')
      .nullish()
      .transform((v) => v ?? null),
  })
  .refine((v) => !v.effectiveTo || v.effectiveTo >= v.effectiveFrom, {
    path: ['effectiveTo'],
    message: 'Ends before it starts',
  })
export type CommissionMemberInput = z.input<typeof commissionMemberInputSchema>

export const commissionMemberUpdateSchema = z.object({
  member: commissionMemberInputSchema,
  version: versionSchema,
})
export type CommissionMemberUpdate = z.input<typeof commissionMemberUpdateSchema>

export interface UserPersonRef {
  id: string
  name: string
  nameAr: string | null
}

export interface CommissionPlanMember {
  id: string
  user: UserPersonRef
  /** The employee linked to the user (`employees.user_id`), when Human resources is on. */
  employee: EmployeeRef | null
  effectiveFrom: string
  effectiveTo: string | null
  monthlyTarget: string | null
  version: number
}

export interface CommissionPlanRate {
  productType: CommissionProductType | null
  category: { id: string; name: string; nameAr: string | null } | null
  rate: string
}

export interface CommissionPlan {
  id: string
  name: string
  nameAr: string | null
  earnerRole: CommissionEarnerRole
  basis: CommissionBasis
  amountBase: CommissionAmountBase
  rate: string
  tierMode: CommissionTierMode
  tiers: CommissionTier[]
  rates: CommissionPlanRate[]
  active: boolean
  notes: string | null
  members: CommissionPlanMember[]
  version: number
}

// ── settings ──────────────────────────────────────────────────────────────────

export const commissionSettingsSchema = z.object({
  /** Maker-checker: whoever calculated a run cannot approve it. */
  requireSeparateApprover: z.boolean(),
  /** With Payroll on, approved commission of an employee linked to the earner is paid on their next payslip. */
  payThroughPayroll: z.boolean(),
})
export type CommissionSettingsValue = z.output<typeof commissionSettingsSchema>

export const DEFAULT_COMMISSION_SETTINGS: CommissionSettingsValue = {
  requireSeparateApprover: true,
  payThroughPayroll: true,
}

// ── runs and statements ───────────────────────────────────────────────────────

/** Months run in order, one after another; REVERSED only while nothing of it is paid or held by payroll. */
export const COMMISSION_RUN_STATUSES = ['DRAFT', 'CALCULATED', 'APPROVED', 'REVERSED', 'CANCELLED'] as const
export type CommissionRunStatus = (typeof COMMISSION_RUN_STATUSES)[number]

/**
 * CALCULATED (in an open run); APPROVED (payable); PAID (directly or by payroll); CARRIED (a negative balance
 * moved into the earner's next statement of the role).
 */
export const COMMISSION_STATEMENT_STATUSES = ['CALCULATED', 'APPROVED', 'PAID', 'CARRIED'] as const
export type CommissionStatementStatus = (typeof COMMISSION_STATEMENT_STATUSES)[number]

export const COMMISSION_PAID_BY = ['DIRECT', 'PAYROLL'] as const
export type CommissionPaidBy = (typeof COMMISSION_PAID_BY)[number]

/**
 * What a statement line is: the month's change on an invoice, debit note or credit note (INVOICED), or on what
 * the customer settled of an invoice (COLLECTED); a REVERSAL of what an earlier month paid on a document whose
 * worth fell (void, return after payment, unallocated or bounced payment), at the amount once paid; or a
 * manual ADJUSTMENT with its reason.
 */
export const COMMISSION_LINE_KINDS = [
  'INVOICE',
  'DEBIT_NOTE',
  'CREDIT_NOTE',
  'COLLECTION',
  'REVERSAL',
  'ADJUSTMENT',
] as const
export type CommissionLineKind = (typeof COMMISSION_LINE_KINDS)[number]

export const commissionRunCreateSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  note: optionalText(500),
})
export type CommissionRunCreateInput = z.input<typeof commissionRunCreateSchema>

export const commissionRunVersionSchema = z.object({ version: versionSchema })
export type CommissionRunVersionInput = z.input<typeof commissionRunVersionSchema>

export const commissionRunReasonSchema = z.object({
  reason: z.string().trim().min(1).max(500),
  version: versionSchema,
})
export type CommissionRunReasonInput = z.input<typeof commissionRunReasonSchema>

/** A manual line on an open run (a correction, a one-off), kept across recalculation. */
export const commissionAdjustmentInputSchema = z.object({
  userId: z.uuid(),
  earnerRole: z.enum(COMMISSION_EARNER_ROLES),
  amount: moneySchema.refine((v) => Number(v) !== 0, 'Cannot be zero'),
  reason: z.string().trim().min(1).max(500),
})
export type CommissionAdjustmentInput = z.input<typeof commissionAdjustmentInputSchema>

export interface CommissionAdjustment {
  id: string
  user: UserPersonRef
  earnerRole: CommissionEarnerRole
  amount: string
  reason: string
  createdAt: string
  createdBy: UserPersonRef | null
}

export const commissionRunListQuerySchema = pageQuerySchema.extend({
  status: z.enum(COMMISSION_RUN_STATUSES).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
})
export type CommissionRunListQuery = z.input<typeof commissionRunListQuerySchema>

export interface CommissionRunListItem {
  id: string
  number: string
  year: number
  month: number
  periodStart: string
  periodEnd: string
  status: CommissionRunStatus
  earners: number
  /** Σ commission earned in the month (signed). */
  earned: string
  /** Σ payable of the positive statements. */
  payable: string
  createdAt: string
}

export interface CommissionRun extends CommissionRunListItem {
  note: string | null
  /** Statements paid, waiting for payment, and carried to the next month. */
  paidCount: number
  unpaidCount: number
  carriedCount: number
  calculatedAt: string | null
  calculatedBy: UserPersonRef | null
  approvedAt: string | null
  approvedBy: UserPersonRef | null
  cancelledAt: string | null
  reversedAt: string | null
  reason: string | null
  journalEntry: { id: string; number: string } | null
  adjustments: CommissionAdjustment[]
  version: number
}

export const commissionPaySchema = z.object({
  paidOn: dateSchema,
  treasuryAccountId: z.uuid(),
  reference: optionalText(100),
  version: versionSchema,
})
export type CommissionPayInput = z.input<typeof commissionPaySchema>

export const commissionStatementListQuerySchema = pageQuerySchema.extend({
  status: z.enum(COMMISSION_STATEMENT_STATUSES).optional(),
  role: z.enum(COMMISSION_EARNER_ROLES).optional(),
  /** One run's statements. */
  runId: z.uuid().optional(),
  /** One earner's statements. */
  userId: z.uuid().optional(),
})
export type CommissionStatementListQuery = z.input<typeof commissionStatementListQuerySchema>

export interface CommissionStatementListItem {
  id: string
  run: { id: string; number: string; year: number; month: number; status: CommissionRunStatus }
  user: UserPersonRef
  employee: EmployeeRef | null
  role: CommissionEarnerRole
  /** Null for a statement that only carries a balance (the earner left the plan). */
  plan: { id: string; name: string; nameAr: string | null } | null
  /** The month's priced base (net sales or gross profit; reversals and adjustments excluded). */
  base: string
  earned: string
  carriedIn: string
  payable: string
  monthlyTarget: string | null
  /** base / target × 100 with 1 decimal; null without a target. */
  achievementPercent: string | null
  status: CommissionStatementStatus
  paidBy: CommissionPaidBy | null
  paidOn: string | null
  payrollRun: { id: string; number: string } | null
  /** An open payroll run holds it (it will be paid on that payslip). */
  heldByPayroll: { id: string; number: string } | null
  lineCount: number
  version: number
}

export interface CommissionStatementLine {
  kind: CommissionLineKind
  /** The sales document the line comes from; null for an adjustment. */
  document: { type: 'INVOICE' | 'CREDIT_NOTE'; id: string; number: string | null; date: string } | null
  customer: { id: string; name: string } | null
  /** The catalogue slice priced: product type and category (null: the document's other lines). */
  productType: CommissionProductType | null
  category: { id: string; name: string; nameAr: string | null } | null
  /** What the document is worth to the earner now, and what approved runs counted before. */
  currentBase: string | null
  countedBefore: string | null
  /** The month's change (signed). */
  base: string
  /** The rate applied, as a percentage (for a reversal: the rate once paid). */
  rate: string
  amount: string
  /** A reversal at the amount once paid, outside the month's tiers. */
  fixed: boolean
  note: string | null
}

export interface CommissionStatement extends CommissionStatementListItem {
  periodStart: string
  periodEnd: string
  basis: CommissionBasis | null
  amountBase: CommissionAmountBase | null
  tierMode: CommissionTierMode | null
  tiers: CommissionTier[]
  /** The rate the tiers gave for the month (null without tiers). */
  tierRate: string | null
  lines: CommissionStatementLine[]
  paymentReference: string | null
  treasuryAccount: { id: string; name: string } | null
  paymentEntry: { id: string; number: string } | null
}

/** Sales by salesperson (`GET /reports/sales-by-salesperson`). */
export const salesBySalespersonQuerySchema = z.object({
  from: dateSchema,
  to: dateSchema,
})
export type SalesBySalespersonQuery = z.input<typeof salesBySalespersonQuerySchema>

export interface SalesBySalespersonRow {
  /** Null: documents without a salesperson. */
  salesperson: UserPersonRef | null
  invoices: number
  netSales: string
  returns: string
  netAfterReturns: string
  grossProfit: string
  /** Receipts allocated in the period to the salesperson's invoices, VAT included. */
  collected: string
}

export interface SalesBySalesperson {
  from: string
  to: string
  rows: SalesBySalespersonRow[]
  totals: Omit<SalesBySalespersonRow, 'salesperson'>
}

/** The refusals commission returns (problem+json `code`), which the web puts in words. */
export const COMMISSION_ERROR_CODES = [
  /** 409: the month already has a commission run. */
  'COMMISSION_PERIOD_TAKEN',
  /** 422: the command does not apply in the run's (or statement's) status. */
  'COMMISSION_RUN_STATUS',
  /** 422: approve a calculated run. */
  'COMMISSION_NOT_CALCULATED',
  /** 422: recalculated at approval, the figures changed: they are saved; review and approve again. */
  'COMMISSION_STALE',
  /** 422: whoever calculated the run cannot approve it (maker-checker setting). */
  'COMMISSION_SAME_PERSON',
  /** 422: months run one after another: an earlier month is open or missing, or a later one is approved. */
  'COMMISSION_OUT_OF_ORDER',
  /** 409: the user is already on a plan of this role for part of these months. */
  'COMMISSION_MEMBER_OVERLAP',
  /** 422: a statement names the member (their user on this plan): end the membership instead. */
  'COMMISSION_MEMBER_IN_USE',
  /** 409: another plan has this name. */
  'COMMISSION_PLAN_NAME_TAKEN',
  /** 422: an open payroll run holds the statement (it will be paid on that payslip). */
  'COMMISSION_IN_PAYROLL',
  /** 422: a run is reversed only while it is the latest approved one and nothing of it is paid or held. */
  'COMMISSION_NOT_REVERSIBLE',
  /** 422: the salesperson must be an active user. */
  'SALESPERSON_INVALID',
] as const
export type CommissionErrorCode = (typeof COMMISSION_ERROR_CODES)[number]
